import { Command } from 'cmdk'
import { Check, Search } from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@maxus/website/utils'

import type { ProjectDrawerExternalProject } from './type'

type ProjectComboboxProps = {
  ariaLabel: string
  disabled?: boolean
  fallbackLabel?: string
  loading?: boolean
  onSearchChange: (value: string) => void
  onValueChange: (value: string) => void
  value?: string
  options: ProjectDrawerExternalProject[]
}

const getProjectLabel = (project: ProjectDrawerExternalProject): string => project.name

export const ProjectCombobox = ({
  ariaLabel,
  disabled = false,
  fallbackLabel,
  loading = false,
  onSearchChange,
  onValueChange,
  options,
  value,
}: ProjectComboboxProps) => {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selectedProject = useMemo(
    () => options.find(project => project.externalId === value),
    [options, value],
  )

  useEffect(
    () => {
      if (!open) {
        setSearch(selectedProject ? getProjectLabel(selectedProject) : (fallbackLabel ?? ''))
      }
    },
    [fallbackLabel, open, selectedProject],
  )

  useEffect(
    () => {
      if (!open) return

      const closeOnOutsidePointerDown = (event: PointerEvent) => {
        if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
      }

      document.addEventListener('pointerdown', closeOnOutsidePointerDown)
      return () => document.removeEventListener('pointerdown', closeOnOutsidePointerDown)
    },
    [open],
  )

  const changeSearch = (nextSearch: string) => {
    setSearch(nextSearch)
    setOpen(true)
    onSearchChange(nextSearch)
    if (selectedProject && nextSearch !== getProjectLabel(selectedProject)) {
      onValueChange('')
    }
  }

  const selectProject = (project: ProjectDrawerExternalProject) => {
    onValueChange(project.externalId)
    setSearch(getProjectLabel(project))
    setOpen(false)
  }

  return (
    <div ref={rootRef}>
      <Command className="relative overflow-visible bg-transparent">
        <Search className="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-text-tertiary" />
        <Command.Input
          aria-label={ariaLabel}
          className={cn(
            'h-11 w-full rounded-md border border-input bg-transparent pr-3 pl-10 text-sm text-text-primary outline-none transition-colors placeholder:text-muted-foreground',
            'focus:border-brand-primary focus:bg-surface-elevated focus:ring-1 focus:ring-focus-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          disabled={disabled}
          onFocus={() => {
            setOpen(true)
            setSearch('')
            onSearchChange('')
          }}
          onValueChange={changeSearch}
          placeholder={t(loading ? 'Loading' : 'Select a project')}
          value={search}
        />
        {open && !loading && (
          <Command.List className="absolute inset-x-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-lg border border-border-subtle bg-surface-card p-1 text-sm text-text-secondary shadow-popover">
            <Command.Empty className="px-3 py-2 text-muted-foreground">{t('No projects found')}</Command.Empty>
            {options.map(project => (
              <Command.Item
                className="relative flex h-9 cursor-default items-center rounded-md px-3 pr-9 outline-none aria-selected:bg-surface-active aria-selected:text-text-primary"
                key={`${project.externalType}:${project.externalIdType}:${project.externalId}`}
                onSelect={() => selectProject(project)}
                value={`${project.name} ${project.externalId}`}
              >
                <span className="truncate">{getProjectLabel(project)}</span>
                {project.externalId === value && <Check className="absolute right-3 size-4 text-text-primary" />}
              </Command.Item>
            ))}
          </Command.List>
        )}
      </Command>
    </div>
  )
}
